import { InfoBlueBorderIcon, WhiteTickIcon } from "../../icon"
import ButtonComponent from "../shared/button/button.component"
import { buttonLabels } from "../shared/constants"
import { ImportEditableRow } from "./import-users-shared.component"
import ImportUsersGrid from "./import-users-grid.component"
import { SeatUsage } from "../../models/organization.model"
import { RoleModel } from "../../models/roles.model"
import { PhoneNumbergroup, PhoneNumberModel } from "../../models/phone-number.model"

interface IProps {
    IsDarkMode: boolean
    RowCount: number
    Rows: ImportEditableRow[]
    ErrorRows: ImportEditableRow[]
    OnRowsChange: (rows: ImportEditableRow[]) => void
    OnBack: () => void
    OnCreate: (summary: { created: number; skipped: number; failed: number }) => void
    Seats: SeatUsage[]
    Roles: RoleModel[]
    PhoneNumbers: PhoneNumberModel[]
    PhoneNumberGroups: PhoneNumbergroup[]
    SelectedOrgId: string
    GetRowErrors: (rows: ImportEditableRow[]) => ImportEditableRow[]
}

const STAT_CARDS = [
    { key: "rowsInFile", label: "Rows in file", color: "var(--color-primary-accent)" },
    { key: "willBeCreated", label: "Will be created", color: "#3CB371" },
    { key: "failedValidation", label: "Failed validation", color: "#D15C5C" },
    { key: "existingSkipped", label: "Existing users, skipped", color: "#E08A2A" }
] as const

const hasRowError = (row?: ImportEditableRow) => {
    if (!row) return false
    const { RowId, rowIndex, ...fields } = row
    return Object.values(fields).some(Boolean)
}

const ValidationReportStep = (props: IProps) => {
    const existingSkipped = props.ErrorRows.filter((row) => row.Email === "Email already exists").length
    const failedValidation = props.ErrorRows.filter((row) => row.Email !== "Email already exists" && hasRowError(row)).length
    const values = {
        rowsInFile: props.RowCount,
        existingSkipped,
        failedValidation,
        willBeCreated: Math.max(props.Rows.length - existingSkipped - failedValidation, 0)
    }

    return (
        <div className="w-p-100 mt-2">
            <div className="d-flex w-p-100">
                {STAT_CARDS.map((card, index) => (
                    <div
                        key={card.key}
                        className={`flex-1 br-3 p-3 border-primary-divider ${props.IsDarkMode ? "bg-color-primary-background" : "bg-color-secondary"} ${index < STAT_CARDS.length - 1 ? "mr-2" : ""}`}
                    >
                        <div
                            className="h1-medium"
                            style={{
                                color: card.color,
                                fontSize: 32,
                                lineHeight: "40px",
                                fontFamily: "silkasemibold"
                            }}
                        >
                            {values[card.key]}
                        </div>
                        <div className="h5-regular color-text-field-label">{card.label}</div>
                    </div>
                ))}
            </div>

            {!!props.Rows.length && (
                <div className={`mt-2 br-3 p-3 border-primary-divider ${props.IsDarkMode ? "bg-color-primary-background" : "bg-color-secondary"}`}>
                    <ImportUsersGrid
                        Rows={props.Rows}
                        ErrorRows={props.ErrorRows}
                        OnRowsChange={props.OnRowsChange}
                        Seats={props.Seats}
                        Roles={props.Roles}
                        PhoneNumbers={props.PhoneNumbers}
                        PhoneNumberGroups={props.PhoneNumberGroups}
                        selectedOrgId={props.SelectedOrgId}
                        GetRowErrors={props.GetRowErrors}
                    />
                </div>
            )}

            <div className={`d-flex align-center justify-between mt-2 px-3 py-2 br-3 border-primary-divider ${props.IsDarkMode ? "bg-color-primary-background" : "bg-color-secondary"}`}>
                <div className="d-flex align-center">
                    <InfoBlueBorderIcon className="flex-shrink-0" />
                    <div className="h5-regular color-text-field-label ml-1">
                        Every created user gets an invitation email and stays Invited until they set a password.
                    </div>
                </div>
                <div className="d-flex align-center flex-shrink-0 ml-2">
                    <button type="button" className="h4-medium color-primary-accent mr-2" onClick={props.OnBack}>
                        {buttonLabels.BACK}
                    </button>
                    <ButtonComponent
                        ButtonLabel={`Create ${values.willBeCreated} ${values.willBeCreated === 1 ? "user" : "users"}`}
                        Variant={values.willBeCreated > 0 ? "primary-btn" : "disabled-btn"}
                        Icon={WhiteTickIcon}
                        ClassName="m-0 pt-2 pb-2 br-2"
                        buttonProps={{ style: { textTransform: "none", fontFamily: "silkamedium", fontSize: "1rem" } }}
                        OnClick={() => props.OnCreate({
                            created: values.willBeCreated,
                            skipped: values.existingSkipped,
                            failed: values.failedValidation
                        })}
                    />
                </div>
            </div>
        </div>
    )
}

export default ValidationReportStep
